/**
 * Generic Range Slider Manager
 * 
 * This utility provides reusable dual-handle range sliders for any numeric tag values.
 * Supports unit conversion (e.g., GB to TB) and integrates with API filtering.
 * 
 * Usage:
 * 1. Include this script in your liquid file
 * 2. Use the range-slider-utils.liquid snippet to create sliders
 * 3. Initialize with: initializeRangeSliders()
 * 4. Use rangeSliderManager.getActiveRangeFilters() in applyFilters()
 * 5. Use rangeSliderManager.resetAllSliders() in resetFilters()
 */

class RangeSliderManager {
    constructor() {
        this.sliders = new Map();
    }

    initializeSlider(container) {
        //console.log('Initializing slider for container:', container);
        
        const tagKey = container.dataset.tagKey;
        const min = parseInt(container.dataset.min);
        const max = parseInt(container.dataset.max);
        const defaultMin = parseInt(container.dataset.defaultMin);
        const defaultMax = parseInt(container.dataset.defaultMax);
        const unitSingular = container.dataset.unitSingular;
        const unitPlural = container.dataset.unitPlural;
        const conversionThreshold = container.dataset.conversionThreshold ? 
            parseInt(container.dataset.conversionThreshold) : null;
        const conversionUnit = container.dataset.conversionUnit;
        const conversionFactor = container.dataset.conversionFactor ? 
            parseInt(container.dataset.conversionFactor) : null;

        //console.log('Slider config:', { tagKey, min, max, defaultMin, defaultMax, unitSingular, conversionThreshold, conversionUnit, conversionFactor });

        const minSlider = container.querySelector('.dual-slider-min');
        const maxSlider = container.querySelector('.dual-slider-max');
        const minValueSpan = container.querySelector('.min-value');
        const maxValueSpan = container.querySelector('.max-value');
        const minLabel = container.querySelector('.min-label');
        const maxLabel = container.querySelector('.max-label');

        //console.log('Found elements:', { 
        //     minSlider: !!minSlider, 
        //     maxSlider: !!maxSlider, 
        //     minValueSpan: !!minValueSpan, 
        //     maxValueSpan: !!maxValueSpan,
        //     minLabel: !!minLabel,
        //     maxLabel: !!maxLabel
        // });

        if (!minSlider || !maxSlider) {
            //console.error('Could not find slider elements!');
            return;
        }

        // Set slider attributes
        minSlider.min = min;
        minSlider.max = max;
        minSlider.value = defaultMin;
        maxSlider.min = min;
        maxSlider.max = max;
        maxSlider.value = defaultMax;

        const sliderConfig = {
            container,
            tagKey,
            minSlider,
            maxSlider,
            minValueSpan,
            maxValueSpan,
            minLabel,
            maxLabel,
            config: {
                min,
                max,
                defaultMin,
                defaultMax,
                unitSingular,
                unitPlural,
                conversionThreshold,
                conversionUnit,
                conversionFactor
            }
        };

        // Format value for display with unit conversion
        const formatValueForDisplay = (value) => {
            if (conversionThreshold && value >= conversionThreshold) {
                const convertedValue = (value / conversionFactor).toFixed(1);
                return `${convertedValue}${conversionUnit}`;
            }
            return `${value}${unitSingular}`;
        };

        // Format value for API (preserves original format)
        const formatValueForAPI = (value) => {
            if (conversionThreshold && value >= conversionThreshold) {
                const convertedValue = (value / conversionFactor).toFixed(1);
                return `${convertedValue}${conversionUnit}`;
            }
            return `${value}${unitSingular}`;
        };

        // Update slider values and display
        const updateSliderValues = () => {
            let minVal = parseInt(minSlider.value);
            let maxVal = parseInt(maxSlider.value);

            //console.log('Updating slider values:', { minVal, maxVal });

            // Ensure min doesn't exceed max
            if (minVal > maxVal) {
                minSlider.value = maxVal;
                minVal = maxVal;
            }

            // Ensure max doesn't go below min
            if (maxVal < minVal) {
                maxSlider.value = minVal;
                maxVal = minVal;
            }

            // Update display values
            const minDisplay = formatValueForDisplay(minVal);
            const maxDisplay = formatValueForDisplay(maxVal);

            //console.log('Formatted values:', { minDisplay, maxDisplay });

            if (minValueSpan) minValueSpan.textContent = `Min: ${minDisplay}`;
            if (maxValueSpan) maxValueSpan.textContent = `Max: ${maxDisplay}`;

            // Update labels if needed
            if (minLabel) minLabel.textContent = formatValueForDisplay(min);
            if (maxLabel) maxLabel.textContent = formatValueForDisplay(max);

            // Update dropdown display text for the specific tag key
            updateDropdownDisplay(tagKey, minDisplay, maxDisplay);
        };

        // Get current values for API
        const getValuesForAPI = () => {
            const minVal = parseInt(minSlider.value);
            const maxVal = parseInt(maxSlider.value);
            return {
                min: formatValueForAPI(minVal),
                max: formatValueForAPI(maxVal),
                minRaw: minVal,
                maxRaw: maxVal
            };
        };

        // Event listeners
        minSlider.addEventListener('input', updateSliderValues);
        maxSlider.addEventListener('input', updateSliderValues);

        // Store configuration
        sliderConfig.updateSliderValues = updateSliderValues;
        sliderConfig.getValuesForAPI = getValuesForAPI;
        sliderConfig.formatValueForDisplay = formatValueForDisplay;
        sliderConfig.formatValueForAPI = formatValueForAPI;

        this.sliders.set(tagKey, sliderConfig);

        // Initial update
        updateSliderValues();
    }

    getSlider(tagKey) {
        return this.sliders.get(tagKey);
    }

    getAllSliders() {
        return this.sliders;
    }

    resetSlider(tagKey) {
        const slider = this.sliders.get(tagKey);
        if (!slider) return;

        slider.minSlider.value = slider.config.defaultMin;
        slider.maxSlider.value = slider.config.defaultMax;
        slider.updateSliderValues();
    }

    resetAllSliders() {
        this.sliders.forEach((slider, tagKey) => {
            this.resetSlider(tagKey);
        });
        // Reset all dropdown display texts
        this.resetAllDropdownDisplays();
    }

    // Reset dropdown display text for all sliders
    resetAllDropdownDisplays() {
        this.sliders.forEach((slider, tagKey) => {
            this.resetDropdownDisplay(tagKey);
        });
    }

    // Reset dropdown display text for a specific slider
    resetDropdownDisplay(tagKey) {
        let displayElementId;
        
        // Map tag keys to their display element IDs
        switch(tagKey) {
            case 'capacity':
                displayElementId = 'capacity-display';
                break;
            case 'processor_speed':
                displayElementId = 'cpu-speed-display';
                break;
            default:
                displayElementId = `${tagKey}-display`;
                break;
        }
        
        const displayElement = document.getElementById(displayElementId);
        if (displayElement) {
            displayElement.textContent = ' - ';
        }
    }

    // Get all active range filters for API
    getActiveRangeFilters() {
        const filters = {};
        
        this.sliders.forEach((slider, tagKey) => {
            const values = slider.getValuesForAPI();
            const config = slider.config;
            
            // Only include range filters if they have been changed from default values
            const minChanged = values.minRaw !== config.defaultMin;
            const maxChanged = values.maxRaw !== config.defaultMax;
            
            if (minChanged || maxChanged) {
                filters[`range_min_${tagKey}`] = values.min;
                filters[`range_max_${tagKey}`] = values.max;
            }
        });
        
        return filters;
    }

    // Restore slider values from URL parameters
    restoreFromUrlParams(urlParams) {
        const rangeParams = {};
        
        // Group range parameters by tag key
        urlParams.forEach(param => {
            const value = new URL(window.location).searchParams.get(param);
            if (param.startsWith('range_min_')) {
                const tagKey = param.replace('range_min_', '');
                if (!rangeParams[tagKey]) rangeParams[tagKey] = {};
                rangeParams[tagKey].min = value;
            } else if (param.startsWith('range_max_')) {
                const tagKey = param.replace('range_max_', '');
                if (!rangeParams[tagKey]) rangeParams[tagKey] = {};
                rangeParams[tagKey].max = value;
            }
        });
        
        // Apply range values to sliders
        Object.entries(rangeParams).forEach(([tagKey, range]) => {
            const slider = this.getSlider(tagKey);
            if (slider) {
                // Convert API values back to slider values
                if (range.min) {
                    const minValue = this.convertAPIValueToSliderValue(range.min, slider);
                    if (minValue !== null) {
                        slider.minSlider.value = minValue;
                    }
                }
                if (range.max) {
                    const maxValue = this.convertAPIValueToSliderValue(range.max, slider);
                    if (maxValue !== null) {
                        slider.maxSlider.value = maxValue;
                    }
                }
                slider.updateSliderValues();
            }
        });
    }

    // Helper function to convert API values back to slider values
    convertAPIValueToSliderValue(apiValue, slider) {
        const config = slider.config;
        
        // Handle unit conversion back to base values
        if (config.conversionUnit && apiValue.includes(config.conversionUnit)) {
            const numericValue = parseFloat(apiValue.replace(config.conversionUnit, ''));
            return Math.round(numericValue * config.conversionFactor);
        } else if (apiValue.includes(config.unitSingular)) {
            return parseInt(apiValue.replace(config.unitSingular, ''));
        }
        
        // Fallback: try to parse as number
        const numericValue = parseFloat(apiValue);
        return isNaN(numericValue) ? null : numericValue;
    }
}

// Global range slider manager - can be used across all filter files
window.rangeSliderManager = window.rangeSliderManager || new RangeSliderManager();

// Generic function to update dropdown display text based on tag key
function updateDropdownDisplay(tagKey, minDisplay, maxDisplay) {
    let displayElementId;
    
    // Map tag keys to their display element IDs
    switch(tagKey) {
        case 'capacity':
            displayElementId = 'capacity-display';
            break;
        case 'processor_speed':
            displayElementId = 'cpu-speed-display';
            break;
        default:
            displayElementId = `${tagKey}-display`;
            break;
    }
    
    const displayElement = document.getElementById(displayElementId);
    if (displayElement) {
        displayElement.textContent = `${minDisplay} - ${maxDisplay}`;
    }
}

// Initialize all range sliders on the page
function initializeRangeSliders() {
    //console.log('Looking for range slider containers...');
    const containers = document.querySelectorAll('.range-slider-container');
    //console.log('Found', containers.length, 'slider containers');
    
    containers.forEach((container, index) => {
        //console.log('Initializing container', index + 1, 'with tag key:', container.dataset.tagKey);
        window.rangeSliderManager.initializeSlider(container);
    });
}

// Generic URL parameter handling for range filters
function updateUrlWithRangeFilters(filters) {
    const url = new URL(window.location);
    
    // Clear existing range parameters dynamically
    const urlParams = Array.from(url.searchParams.keys());
    urlParams.forEach(param => {
        if (param.startsWith('range_min_') || param.startsWith('range_max_')) {
            url.searchParams.delete(param);
        }
    });
    
    // Add active filters to URL
    Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'none') {
            url.searchParams.set(key, value);
        }
    });
    
    // Update browser URL without page reload
    window.history.pushState({}, '', url);
}

function restoreRangeFiltersFromUrl() {
    if (!window.rangeSliderManager) return {};
    
    const url = new URL(window.location);
    const urlParams = Array.from(url.searchParams.keys());
    const rangeFilters = {};
    
    // Find range parameters and restore their values
    const rangeParams = urlParams.filter(param => 
        param.startsWith('range_min_') || param.startsWith('range_max_')
    );
    
    if (rangeParams.length > 0) {
        window.rangeSliderManager.restoreFromUrlParams(rangeParams);
        
        // Build filters object for API
        rangeParams.forEach(param => {
            rangeFilters[param] = url.searchParams.get(param);
        });
    }
    
    return rangeFilters;
}